import { useCallback, useEffect, useRef, useState } from "react";
import { GridEditModal } from "../../components/editSearch/gridEditModal.tsx";
import { servicePost } from "../../api/client.ts";
import styles from "../../styles/formPage.module.scss";

/* ── Types ──────────────────────────────────────────────── */

interface ProductExternal {
  ProductID: number;
  OperatorID: number;
  ExternalName: string;
  ExternalDescription: string;
  ExternalArticleNr: string;
  Deleted: boolean;
}

interface Props {
  open: boolean;
  onClose: () => void;
  productId: number;
  operatorId: number;
}

/* ── Component ──────────────────────────────────────────── */

export function ExternalInfoModal({ open, onClose, productId, operatorId }: Props) {
  const [name, setName] = useState("");
  const [articleNr, setArticleNr] = useState("");
  const [description, setDescription] = useState("");
  const [deleted, setDeleted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  // Dedupe in-flight fetch so StrictMode's dev double-mount doesn't fire two
  // WebGetProductExternal calls. Reused if composite key matches.
  const inFlightRef = useRef<{ key: string; promise: ReturnType<typeof servicePost<ProductExternal>> } | null>(null);
  useEffect(() => {
    if (!open || productId <= 0 || operatorId <= 0) return;
    setLoading(true);
    setError(null);
    setSuccess(false);
    setName("");
    setArticleNr("");
    setDescription("");
    setDeleted(false);
    let cancelled = false;
    const key = `${productId}|${operatorId}`;
    let promise: ReturnType<typeof servicePost<ProductExternal>>;
    if (inFlightRef.current?.key === key) {
      promise = inFlightRef.current.promise;
    } else {
      promise = servicePost<ProductExternal>("WebGetProductExternal", { productId, operatorId });
      inFlightRef.current = { key, promise };
    }
    promise
      .then(result => {
        if (cancelled) return;
        if (!result.bError && result.oData) {
          setName(result.oData.ExternalName ?? "");
          setArticleNr(result.oData.ExternalArticleNr ?? "");
          setDescription(result.oData.ExternalDescription ?? "");
          setDeleted(result.oData.Deleted ?? false);
        }
      })
      .catch(() => { if (!cancelled) setError("Failed to load external info"); })
      .finally(() => {
        if (!cancelled) setLoading(false);
        if (inFlightRef.current?.promise === promise) inFlightRef.current = null;
      });
    return () => { cancelled = true; };
  }, [open, productId, operatorId]);

  const handleSave = useCallback(async () => {
    setSaving(true);
    setError(null);
    setSuccess(false);
    try {
      const data: ProductExternal = {
        ProductID: productId,
        OperatorID: operatorId,
        ExternalName: name,
        ExternalDescription: description,
        ExternalArticleNr: articleNr,
        Deleted: deleted,
      };
      // Parameter is named `pl` server-side; sent as `data` the whole payload was dropped.
      const result = await servicePost<boolean>("WebSaveProductExternal", { pl: data });
      if (result.bError) {
        setError(result.sMessage ?? "Failed to save");
      } else {
        setSuccess(true);
      }
    } catch {
      setError("Failed to save external info");
    } finally {
      setSaving(false);
    }
  }, [productId, operatorId, name, articleNr, description, deleted]);

  const status = error ? { type: "error" as const, message: error }
    : success ? { type: "success" as const, message: "Saved" }
    : null;

  return (
    <GridEditModal
      open={open}
      title={`Edit External Info (OID: ${operatorId})`}
      loading={loading}
      saving={saving}
      statusMessage={status}
      onClose={onClose}
      onSave={handleSave}
    >
      <p className={styles.hint} style={{ marginBottom: "var(--space-3)" }}>
        The external fields are not used on all kiosk software. It depends on if you have requested additional features.
      </p>
      <div className={styles.fieldRowStacked}>
        <label>Name</label>
        <input className={styles.fieldInput} value={name} onChange={e => setName(e.target.value)} />
      </div>
      <div className={styles.fieldRowStacked}>
        <label>Article Nr</label>
        <input className={styles.fieldInput} value={articleNr} onChange={e => setArticleNr(e.target.value)} />
      </div>
      <div className={styles.fieldRowStacked}>
        <label>Description</label>
        <textarea className={styles.fieldTextarea} value={description} onChange={e => setDescription(e.target.value)} rows={3} />
      </div>
      <div className={styles.fieldRowStacked}>
        <label style={{ display: "flex", alignItems: "center", gap: "var(--space-1)" }}>
          <input type="checkbox" checked={deleted} onChange={e => setDeleted(e.target.checked)} />
          Delete
        </label>
      </div>
    </GridEditModal>
  );
}
